import {
  CATEGORY_MAX,
  CATEGORY_SEPARATORS,
  NOTE_CATEGORIES_MAX,
  MAIL_DOMAINS,
  RESERVED_PREFIXES,
  RESERVED_USERNAMES,
  ROLE_MAILBOXES,
  USERNAME_MAX,
  USERNAME_MIN,
} from "./constants";

export interface ParsedAddress {
  username: string;
  /** Normalized category slug, or null when none was given or it was invalid. */
  category: string | null;
  /** Category exactly as written in the address, for logging. */
  rawCategory: string | null;
  domain: string;
}

const USERNAME_RE = /^[a-z0-9][a-z0-9._-]*$/;
// Letters/numbers in any script (so WeCom/X hashtags like #项目A work), plus - _ .
const CATEGORY_RE = /^[\p{L}\p{N}][\p{L}\p{N}_.-]*$/u;

export function normalizeUsername(input: string): string {
  return input.trim().toLowerCase();
}

export function isValidUsername(username: string): boolean {
  return (
    username.length >= USERNAME_MIN &&
    username.length <= USERNAME_MAX &&
    USERNAME_RE.test(username) &&
    !/[._-]$/.test(username)
  );
}

/** Reserved words compared with . _ - removed, so "post.master" and "no_reply" are caught too. */
const RESERVED_COMPACT = new Set([...RESERVED_USERNAMES].map(compact));

export function isReservedUsername(username: string): boolean {
  const name = compact(normalizeUsername(username));
  return RESERVED_COMPACT.has(name) || RESERVED_PREFIXES.some((prefix) => name.startsWith(prefix));
}

/**
 * The role mailbox (e.g. "postmaster", "hi") an address is sent to, or null.
 * Checked before parseAddress because some role names are shorter than a valid username.
 * Categories are ignored: "support+billing@" still goes to support.
 */
export function roleMailbox(address: string, domains: readonly string[] = MAIL_DOMAINS): string | null {
  const trimmed = address.trim().replace(/^<|>$/g, "");
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || !domains.includes(trimmed.slice(at + 1).toLowerCase())) return null;
  const local = trimmed.slice(0, at);
  const sepIndex = firstSeparatorIndex(local);
  const name = normalizeUsername(sepIndex === -1 ? local : local.slice(0, sepIndex));
  return ROLE_MAILBOXES.has(name) ? name : null;
}

/** Turn a user-typed category into its slug, or null when it can't be one. */
export function normalizeCategory(input: string | null | undefined): string | null {
  if (!input) return null;
  const slug = input.normalize("NFKC").trim().replace(/^#+/, "").toLowerCase();
  if (!slug || [...slug].length > CATEGORY_MAX || !CATEGORY_RE.test(slug)) return null;
  return slug;
}

/**
 * Turn user-typed categories into unique slugs, in order: a list, or one string like "#travel japan, food".
 * Invalid ones are dropped; at most NOTE_CATEGORIES_MAX are kept.
 */
export function normalizeCategories(input: string | ReadonlyArray<string | null | undefined> | null | undefined): string[] {
  const parts = (typeof input === "string" ? [input] : (input ?? [])).flatMap((s) => (s ?? "").normalize("NFKC").split(/[\s,，、]+/));
  const slugs = parts.map(normalizeCategory).filter((s): s is string => s !== null);
  return [...new Set(slugs)].slice(0, NOTE_CATEGORIES_MAX);
}

/**
 * Parse a recipient like `Aaron#Shopping@ccnote.me` or `aaron+shopping@ccnote.me`.
 * Returns null when the address isn't on one of our domains or the username is malformed.
 */
export function parseAddress(
  address: string,
  domains: readonly string[] = MAIL_DOMAINS,
): ParsedAddress | null {
  const trimmed = address.trim().replace(/^<|>$/g, "");
  const at = trimmed.lastIndexOf("@");
  if (at <= 0) return null;

  const domain = trimmed.slice(at + 1).toLowerCase();
  if (!domains.includes(domain)) return null;

  const local = trimmed.slice(0, at);
  const sepIndex = firstSeparatorIndex(local);
  const userPart = sepIndex === -1 ? local : local.slice(0, sepIndex);
  const rawCategory = sepIndex === -1 ? null : local.slice(sepIndex + 1) || null;

  const username = normalizeUsername(userPart);
  if (!isValidUsername(username)) return null;

  return { username, category: normalizeCategory(rawCategory), rawCategory, domain };
}

/** Build the address a user should send to for a category. */
export function formatAddress(
  username: string,
  category?: string | null,
  separator: (typeof CATEGORY_SEPARATORS)[number] = "#",
  domain: string = MAIL_DOMAINS[0],
): string {
  return category ? `${username}${separator}${category}@${domain}` : `${username}@${domain}`;
}

/** First `#tag` in free text, e.g. "@ccnote #shopping please" → "shopping". */
export function categoryFromText(text: string): string | null {
  const match = text.normalize("NFKC").match(/(?:^|\s)#([\p{L}\p{N}][\p{L}\p{N}_.-]*)/u);
  return normalizeCategory(match?.[1]);
}

function compact(name: string): string {
  return name.replace(/[._-]/g, "");
}

function firstSeparatorIndex(local: string): number {
  const indexes = CATEGORY_SEPARATORS.map((s) => local.indexOf(s)).filter((i) => i !== -1);
  return indexes.length ? Math.min(...indexes) : -1;
}
